import { useAtom } from "jotai";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Text } from "chameleon/lib/components";

import {
  paymentMessagesAtom,
  paymentRequirementsAtom,
} from "checkout-experience/atoms/payment-atoms";
import { Modal } from "checkout-experience/components/modal/modal";
import { PayByBankAccountType } from "checkout-experience/components/payment-method/pay-options/pay-by-bank/types";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { BrowserSettings } from "checkout-query-client/api/types";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";

import { sendCustomAction } from "datadog-loader/loader";

import { getIsFeatureEnabled } from "splitio-loader/src/utils/get-split-io-flag";

import {
  finicityConnectContainer,
  finicitySDKModalContainer,
  payByBankResult,
  payByBankResultContainer,
} from "./modal-pay-by-bank-sdk.css";

interface ModalPayByBankSDKProps {
  browserSettings: BrowserSettings;
  setBrowserSettings: Dispatch<SetStateAction<BrowserSettings>>;
}

export const ModalPayByBankSDK = ({
  browserSettings,
  setBrowserSettings,
}: ModalPayByBankSDKProps) => {
  const [paymentRequirements, setPaymentRequirements] = useAtom(
    paymentRequirementsAtom
  );
  const { target } = useChexContext();
  const [paymentMessages, setPaymentMessages] = useAtom(paymentMessagesAtom);

  const [iFrameSrc, setIFrameSrc] = useState<string | undefined>();
  const [hasIframeContentLoaded, setHasIframeContentLoaded] = useState(false);
  const [isFinicityModalOpen, setIsFinicityModalOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (
      !browserSettings?.isModalOpen &&
      !isFinicityModalOpen &&
      paymentRequirements?.payByBankConsentUrl &&
      !paymentMessages.payByBankCompletionMessage
    ) {
      const payByBankConsentUrlParams = new URLSearchParams(
        paymentRequirements?.payByBankConsentUrl
      );
      const payByBankConsentUrlRedirectUri =
        payByBankConsentUrlParams.get("redirectUri");

      setIsFinicityModalOpen(true);

      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      window?.finicityConnect?.launch(paymentRequirements.payByBankConsentUrl, {
        selector: "#finicity-connect-container",
        overlay: "position: absolute;top: 0;left: 0;width: 100%;height: 100%;",
        redirectUrl: payByBankConsentUrlRedirectUri,
        user: (event: { type: string; data: unknown }) => {
          if (getIsFeatureEnabled(target, "additional-logging")) {
            sendCustomAction({
              name: "PayByBankFinicityConnectUserEvent",
              context: {
                eventData: JSON.stringify(event?.data),
              },
            });
          }
        },
        success: () => {
          if (getIsFeatureEnabled(target, "additional-logging")) {
            sendCustomAction({
              name: "PayByBankFinicityConnectSuccessEvent",
              context: {
                redirectUri: payByBankConsentUrlRedirectUri,
              },
            });
          }
          if (payByBankConsentUrlRedirectUri) {
            setIFrameSrc(payByBankConsentUrlRedirectUri);
          }
        },
      });
    } else {
      handleCloseModal();
    }
  }, [paymentRequirements, paymentMessages]);

  useEffect(() => {
    if (iFrameSrc) {
      // open checkout modal for completion handling
      setBrowserSettings((prevSettings) => ({
        ...prevSettings,
        isModalOpen: true,
      }));
      setIsFinicityModalOpen(false);
    }
  }, [iFrameSrc]);

  const handleCloseModal = () => {
    // close modal overlay and reset everything
    setBrowserSettings((prevSettings) => ({
      ...prevSettings,
      isModalOpen: false,
    }));
    setIsFinicityModalOpen(false);
    setHasIframeContentLoaded(false);
    setIFrameSrc(undefined);
    // @ts-ignore
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    window?.finicityConnect?.destroy();
  };

  const handleIframeLoad = () => {
    setHasIframeContentLoaded(true);
  };

  useEffect(() => {
    // useEffect for Pay by Bank completion listener
    const postMessageListener = (event: {
      type: string;
      data: {
        event: string;
        type?: string;
        reason?: string;
        payload?: {
          payResult?: {
            paymentMethod: {
              paymentMethodType: string; // TODO PBB: make enum
              payByBank: {
                accountType: PayByBankAccountType;
                institutionalLogo: string;
                last4digitsOfAccountNumber: string;
                bankName: string;
                payByBankRequestId: string;
              };
            };
          };
          accountLinkingRequestId: string;
        };
      };
    }) => {
      if (getIsFeatureEnabled(target, "additional-logging")) {
        sendCustomAction({
          name: "PayByBankCompletionEvent",
          context: {
            event: event.data.event,
            payByBankRequestId:
              event.data.payload?.payResult?.paymentMethod?.payByBank
                ?.payByBankRequestId,
            accountLinkingRequestId:
              event.data.payload?.accountLinkingRequestId,
          },
        });
      }
      if (event?.data?.event === "Pbb-completion") {
        setPaymentMessages({
          ...paymentMessages,
          payByBankCompletionMessage: event.data,
        });
        setPaymentRequirements({
          ...paymentRequirements,
          payByBankConsentUrl: undefined,
        });
      } else if (
        event?.data?.type === "cancel" ||
        event?.data?.type === "error"
      ) {
        // user exits using finicity content controls
        setPaymentMessages({
          ...paymentMessages,
          payByBankCompletionMessage: event.data,
        });

        handleCloseModal();
      } else if (event?.data?.event === "Pbb-failure") {
        setPaymentRequirements({
          ...paymentRequirements,
          payByBankConsentUrl: undefined,
        });

        usePubSub().publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PayByBankCompletionError,
        });

        handleCloseModal();
      }
      return;
    };
    window.addEventListener("message", postMessageListener);

    // cleanup on unmount
    return () => {
      window.removeEventListener("message", postMessageListener);

      handleCloseModal();
    };
  }, []);

  if (iFrameSrc) {
    // conditionally render checkout modal
    return (
      <Modal
        data-testid="modal-pbb"
        wrapperId="modal-pbb-wrapper"
        closeModalHandler={handleCloseModal}
        isModalOpen={browserSettings?.isModalOpen}
        title={t("payByBank.linkBankAccount")}
        width="90vw"
        height="80vw"
        maxWidth="500px"
      >
        <div className={payByBankResultContainer}>
          <Text styleVariant="body" colorVariant="primaryText">
            {t("payByBank.accountLinkingInProgress")}
          </Text>

          <iframe
            data-testid="pbb-result"
            className={payByBankResult}
            title="pbb-result"
            src={iFrameSrc}
            style={{
              display: hasIframeContentLoaded ? "block" : "none",
            }}
            onLoad={handleIframeLoad}
          />
        </div>
      </Modal>
    );
  } else if (!iFrameSrc) {
    // conditionally render finicity modal
    return (
      <div
        id="finicity-sdk-modal-container"
        data-testid="finicity-sdk-modal-container"
        className={finicitySDKModalContainer}
        style={isFinicityModalOpen ? { display: "block" } : { display: "none" }}
      >
        {/* This container must be available and will be injected with modal content from finicity */}
        <div
          id="finicity-connect-container"
          data-testid="finicity-connect-container"
          className={finicityConnectContainer}
        ></div>
      </div>
    );
  } else {
    return;
  }
};
